import React, { useState, useEffect } from 'react';

interface AnamneseFormProps {
  student: any;
  onSave: (updatedAnamnesis: any) => Promise<void>;
}

export const AnamneseForm: React.FC<AnamneseFormProps> = ({ student, onSave }) => {
  const [formType, setFormType] = useState<'adult' | 'child'>(student?.anamnesis?.type || 'adult');
  const [answers, setAnswers] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (student?.anamnesis?.answers && student.anamnesis.type === formType) {
      setAnswers(student.anamnesis.answers);
    } else {
      setAnswers({});
    }
  }, [student, formType]);

  const handleChange = (key: string, value: any) => {
    setAnswers((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleCheckboxChange = (key: string, value: string) => {
    setAnswers((prev: any) => {
      const current = prev[key] || [];
      if (current.includes(value)) {
        return { ...prev, [key]: current.filter((v: string) => v !== value) };
      }
      return { ...prev, [key]: [...current, value] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    const updatedAnamnesis = {
      status: 'APPROVED',
      type: formType,
      answers: answers
    };
    await onSave(updatedAnamnesis);
    setIsSaving(false);
  };

  const renderRadioGroup = (label: string, key: string, options: string[]) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontSize: '14px', cursor: 'pointer' }}>
            <input 
              type="radio" 
              name={key} 
              value={opt} 
              checked={answers[key] === opt} 
              onChange={(e) => handleChange(key, e.target.value)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary-color)' }}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  const renderCheckboxGroup = (label: string, key: string, options: string[]) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontSize: '14px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              value={opt} 
              checked={(answers[key] || []).includes(opt)} 
              onChange={() => handleCheckboxChange(key, opt)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--primary-color)' }}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  const renderTextInput = (label: string, key: string, placeholder?: string, multiLine = false) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>{label}</label>
      {multiLine ? (
        <textarea 
          value={answers[key] || ''} 
          onChange={(e) => handleChange(key, e.target.value)} 
          placeholder={placeholder}
          rows={3}
          style={{ width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', resize: 'vertical' }}
        />
      ) : (
        <input 
          type="text" 
          value={answers[key] || ''} 
          onChange={(e) => handleChange(key, e.target.value)} 
          placeholder={placeholder}
          style={{ width: '100%', padding: '12px 16px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white' }}
        />
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        <button 
          type="button"
          onClick={() => setFormType('adult')}
          style={{ flex: 1, padding: '12px', background: formType === 'adult' ? 'var(--primary-color)' : 'rgba(0,0,0,0.2)', color: formType === 'adult' ? 'var(--text-dark)' : 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
        >
          Anamnese Adulto
        </button>
        <button 
          type="button"
          onClick={() => setFormType('child')}
          style={{ flex: 1, padding: '12px', background: formType === 'child' ? 'var(--primary-color)' : 'rgba(0,0,0,0.2)', color: formType === 'child' ? 'var(--text-dark)' : 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
        >
          Anamnese Criança (5 a 12 anos)
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {formType === 'adult' ? (
          <>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>1. Perfil Esportivo & Histórico no Tênis</h4>
              {renderRadioGroup('Lateralidade', 'handedness', ['Destro', 'Canhoto'])}
              {renderRadioGroup('Empunhadura Principal', 'grip', ['Continental', 'Eastern', 'Semi-Western', 'Western', 'Não Sabe'])}
              {renderRadioGroup('Backhand', 'backhand', ['Uma mão', 'Duas mãos'])}
              {renderRadioGroup('Nível de Experiência Atual', 'experienceLevel', [
                'Iniciante: Nunca jogou ou fez poucas aulas experimentais; aprendendo empunhaduras e golpes básicos.',
                'Intermediário: Mantém troca de bolas, saca com regularidade, inicia jogos de contagem de pontos.',
                'Avançado: Domina variação de efeitos (topspin/slice), controle de profundidade e táticas de jogo.',
                'Competitivo: Disputa torneios locais, estaduais ou rankings internos de clubes/arenas.'
              ])}
              {renderTextInput('Tempo de prática no tênis', 'practiceTime')}
              {renderTextInput('Outros esportes praticados atualmente ou no passado', 'otherSports')}
              {renderRadioGroup('Frequência semanal pretendida', 'frequency', ['1x/semana', '2x/semana', '3x+ /semana'])}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>2. Histórico de Saúde & Condicionamento Físico</h4>
              {renderRadioGroup('Pratica atividade física regularmente além do tênis?', 'physicalActivity', ['Sim', 'Não'])}
              {answers.physicalActivity === 'Sim' && renderTextInput('Qual(is) e com que frequência?', 'physicalActivityDetails')}
              
              {renderRadioGroup('Possui histórico de lesões ortopédicas ou dores recorrentes?', 'hasInjuries', ['Sim', 'Não'])}
              {answers.hasInjuries === 'Sim' && (
                <>
                  {renderCheckboxGroup('Áreas afetadas (atuais ou passadas)', 'injuryAreas', [
                    'Cotovelo (Epicondilite / Tennis Elbow)',
                    'Ombro / Manguito Rotador',
                    'Pulso / Punho',
                    'Coluna (Lombar / Cervical)',
                    'Joelho / Tornozelo / Fisioterapia recente'
                  ])}
                  {renderTextInput('Observações sobre lesões', 'injuryObservations', '', true)}
                </>
              )}
              
              {renderCheckboxGroup('Condições Clínicas de Atenção', 'medicalConditions', [
                'Hipertensão',
                'Diabetes',
                'Problemas Cardíacos',
                'Asma / Problemas Respiratórios'
              ])}
              {renderTextInput('Uso contínuo de medicamentos (listar)', 'medications', '', true)}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>3. Equipamento Atual</h4>
              {renderTextInput('Raquete (Marca / Modelo)', 'racketModel')}
              {renderTextInput('Encordoamento Atual (Tipo de corda e Tensão)', 'strings')}
              {renderTextInput('Frequência de troca de cordas', 'stringFrequency')}
              {renderRadioGroup('Calçado: Utiliza tênis específico para quadra?', 'shoes', ['Sim', 'Não'])}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>4. Objetivos com as Aulas</h4>
              {renderCheckboxGroup('Qual é a sua principal motivação no tênis? (Selecione as principais)', 'motivation', [
                'Condicionamento físico, perda de peso e saúde.',
                'Lazer, socialização e alívio de estresse.',
                'Aperfeiçoamento técnico e biomecânico.',
                'Preparação para torneios, ranking e jogos competitivos.'
              ])}
              {renderTextInput('Expectativas específicas com o professor', 'expectations', '', true)}
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '16px', marginBottom: '16px', border: '1px solid var(--primary-color)' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>5. Parecer Técnico Inicial (Uso Exclusivo do Professor)</h4>
              {renderTextInput('Avaliação de Mobilidade e Biomecânica', 'mobility', '', true)}
              {renderTextInput('Pontos Fortes Identificados', 'strengths', '', true)}
              {renderTextInput('Principais Ajustes Prioritários (Curto Prazo)', 'adjustments', '', true)}
              {renderTextInput('Plano de Desenvolvimento Sugerido', 'plan', '', true)}
            </div>
          </>
        ) : (
          <>
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>1. Perfil da Criança & Experiência</h4>
              {renderRadioGroup('A criança já praticou tênis antes?', 'practiceBefore', [
                'Sim, já fez aulas',
                'Sim, brinca com os pais',
                'Não, primeira vez'
              ])}
              {renderRadioGroup('Como ela lida com esportes em geral?', 'sportsHandling', [
                'Muito ativa/competitiva',
                'Gosta de brincar e socializar',
                'Um pouco tímida/introvertida'
              ])}
              {renderCheckboxGroup('Pratica outras atividades atualmente?', 'otherActivities', [
                'Futebol', 'Natação', 'Lutas', 'Dança'
              ])}
              {renderTextInput('Outras atividades (especificar)', 'otherActivitiesDetails')}
              {renderRadioGroup('Frequência semanal desejada', 'frequency', ['1x/semana', '2x/semana', '3x+/semana'])}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>2. Saúde, Condicionamento e Atenção</h4>
              {renderRadioGroup('Tem alguma restrição médica ou condição de saúde?', 'healthConditions', [
                'Nenhuma',
                'Asma/Alergia',
                'Restrição motora',
                'TDAH / Espectro',
                'Outras'
              ])}
              {answers.healthConditions === 'Outras' && renderTextInput('Especifique a condição', 'healthConditionsDetails')}
              {renderRadioGroup('Usa óculos ou tem alguma dificuldade de visão/audição?', 'glasses', ['Sim', 'Não'])}
              {renderRadioGroup('Lateralidade (se já definida)', 'handedness', ['Destro', 'Canhoto', 'Ambidestro', 'Ainda não definida'])}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>3. Equipamento</h4>
              {renderRadioGroup('Possui raquete própria adequada para a idade (cores)?', 'racket', [
                'Sim',
                'Não, precisará emprestada',
                'Não sei avaliar'
              ])}
              {renderRadioGroup('Calçado adequado para esportes?', 'shoes', ['Sim', 'Não'])}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>4. Objetivos dos Pais / Responsáveis</h4>
              {renderCheckboxGroup('Qual a principal expectativa com as aulas?', 'parentExpectations', [
                'Desenvolvimento motor e coordenação',
                'Disciplina, respeito a regras e foco',
                'Diversão e socialização',
                'Introdução ao esporte visando competição futura'
              ])}
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '16px', marginBottom: '16px', border: '1px solid var(--primary-color)' }}>
              <h4 style={{ color: 'var(--primary-color)', margin: '0 0 16px 0' }}>5. Parecer Inicial do Professor (Exclusivo)</h4>
              {renderTextInput('Coordenação Motora Geral', 'coordination', '', true)}
              {renderTextInput('Nível de Atenção e Foco', 'attention', '', true)}
              {renderTextInput('Empolgação/Engajamento na quadra', 'engagement', '', true)}
              {renderTextInput('Plano de Desenvolvimento (Bolas vermelhas, laranjas, etc.)', 'plan', '', true)}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button type="submit" disabled={isSaving} style={{ padding: '12px 32px', background: 'var(--primary-color)', color: 'var(--text-dark)', borderRadius: '100px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '16px' }}>
            {isSaving ? 'Salvando...' : 'Salvar Anamnese'}
          </button>
        </div>
      </form>
    </div>
  );
};
